
import React, { useState, useEffect, useCallback } from 'react';
import api_service from "../../network/api_callbacks";
import { toast } from 'react-toastify';
import { Close as CloseIcon } from '@mui/icons-material';
import storage, { LS_LOGIN } from "../../storage/storage";
import {
    Box,
    Button,
    InputAdornment,
    IconButton,
    TextField,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Pagination,
    Grid,
    CircularProgress,
    Typography
} from "@mui/material";
import { styled } from '@mui/material/styles';
import Header from "../../components/Header";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    fontFamily: 'Inter, sans-serif',
    fontWeight: 400,
    fontSize: '0.75rem',
    lineHeight: 1.43,
    display: 'table-cell',
    verticalAlign: 'inherit',
    borderBottom: '1px solid rgba(224, 224, 224, 1)',
    textAlign: 'left',
    padding: '16px',
    color: 'rgba(0, 0, 0, 0.87)',
    paddingBottom: '16px', // Increased padding
    paddingTop: '16px', // Increased padding
}));


const SalesDashboard = () => {
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);
    const [totalAmount, setTotalAmount] = useState(0);

    // Modal State removed as it was unused


    const [filters, setFilters] = useState({
        userId: '',
        transactionId: '',
        // status: 'success', // Implied by endpoint, but keeping for display or if API allows override
        paymentGateway: '',
        platform: '',
        eventType: '',
        type: '',
        currency: '',
        startDate: '',
        endDate: '',
        minAmount: '',
        maxAmount: '',
        mobileNo: '' // New filter
    });

    const fetchTransactions = useCallback(async () => {
        setLoading(true);
        try {
            const params = {
                page,
                limit: 8,
                ...filters
            };

            // Validation: Ensure Min <= Max for Amount
            if (params.minAmount && params.maxAmount && Number(params.minAmount) > Number(params.maxAmount)) {
                setLoading(false);
                toast.error("Min Amount cannot be greater than Max Amount");
                return;
            }

            // Validation: Ensure Start Date <= End Date
            if (params.startDate && params.endDate && new Date(params.startDate) > new Date(params.endDate)) {
                setLoading(false);
                toast.error("Start Date cannot be later than End Date");
                return;
            }

            // Remove empty filters
            Object.keys(params).forEach(key => {
                if (params[key] === '' || params[key] === null || params[key] === undefined) {
                    delete params[key];
                }
            });



            const response = await api_service.getSalesTransactions(params);

            if (response && response.success) {
                setTransactions(response.data);
                setTotalPages(response.pagination.pages);
                setTotalRecords(response.pagination.total);
                setTotalAmount(response.totalAmount || 0);
            } else {
                setTransactions([]);
                setTotalAmount(0);
            }
        } catch (error) {
            console.error("Error fetching transactions:", error);
            toast.error("Failed to fetch transactions");
        } finally {
            setLoading(false);
        }
    }, [page, filters]);

    // Debounce Effect for Dynamic Filtering
    useEffect(() => {
        const handler = setTimeout(() => {
            setPage(1); // Reset to page 1 on filter change
            fetchTransactions();
        }, 500); // 500ms debounce

        return () => {
            clearTimeout(handler);
        };
    }, [filters]); // Trigger on filters change

    useEffect(() => {
        fetchTransactions();
    }, [fetchTransactions]);

    // Disable body scroll for this page
    useEffect(() => {
        const originalOverflow = document.body.style.overflow;
        document.body.style.overflowX = 'hidden';
        return () => {
            document.body.style.overflow = originalOverflow;
        };
    }, []);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const handleClearField = (fieldName) => {
        setFilters(prev => ({ ...prev, [fieldName]: '' }));
    };

    const handleClearFilters = () => {
        setFilters({
            userId: '',
            transactionId: '',
            // status: 'success',
            paymentGateway: '',
            platform: '',
            eventType: '',
            type: '',
            currency: '',
            startDate: '',
            endDate: '',
            minAmount: '',
            maxAmount: '',
            mobileNo: ''
        });
        setPage(1);
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return 'Invalid Date';
            const month = date.toLocaleString('default', { month: 'short' });
            const day = date.getDate();
            const hours = date.getHours();
            const minutes = date.getMinutes().toString().padStart(2, '0');
            const seconds = date.getSeconds().toString().padStart(2, '0');
            const ampm = hours >= 12 ? 'PM' : 'AM';
            const displayHours = hours % 12 || 12;
            return `${month} ${day} ${displayHours}:${minutes}:${seconds} ${ampm}`;
        } catch (e) {
            return 'Invalid Date';
        }
    };

    // Columns required: Userid , platfrom, start,ends date , currentcy ,and phoneNumber
    // Plus "show vlaues of those rows ehere transaction status i success" (Status column might be redundant if all are success, but good to show)

    return (
        <div id='sales-dashboard'
            style={{
                height: '100%', // Use full height available from parent
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                paddingTop: '25px',
                maxWidth: '100%', // Allow utilizing more space if parent permits
                boxSizing: 'border-box'
            }}>
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
                <Header title="" subtitle={`List of Sales Transactions (${totalRecords})`} />

                <Box sx={{ px: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h2 style={{ textAlign: "left", margin: 0 }}>SALES DASHBOARD</h2>
                    <Paper elevation={3} sx={{ p: 1, px: 2, bgcolor: '#f5f5f5', border: '1px solid #ddd', m: '20px' }}>
                        <Typography variant="h6" component="span" fontWeight="bold">
                            Total Amount:
                        </Typography>
                        <Typography variant="h6" component="span" fontWeight="bold" sx={{ ml: 1, color: 'black' }}>
                            {totalAmount}
                        </Typography>
                    </Paper>
                </Box>

                {/* Filters Section */}
                <div id='sales-dashboard-filters'
                    style={{
                        padding: '10px 20px',
                        flexShrink: 0
                    }}>
                    <Box
                        p="20px"
                        component={Paper}
                        elevation={3}
                    >
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6} md={3}>
                                <TextField
                                    fullWidth
                                    label="User ID"
                                    name="userId"
                                    value={filters.userId}
                                    onChange={handleFilterChange}
                                    size="small"
                                    InputLabelProps={{ shrink: true, sx: { "&.Mui-focused": { color: "rgba(0, 0, 0, 0.6)" } } }}
                                    InputProps={{
                                        endAdornment: filters.userId && (
                                            <InputAdornment position="end">
                                                <IconButton onClick={() => handleClearField('userId')} edge="end">
                                                    <CloseIcon />
                                                </IconButton>
                                            </InputAdornment>
                                        )
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <TextField
                                    fullWidth
                                    label="Mobile No"
                                    name="mobileNo"
                                    value={filters.mobileNo}
                                    onChange={handleFilterChange}
                                    size="small"
                                    InputLabelProps={{ shrink: true, sx: { "&.Mui-focused": { color: "rgba(0, 0, 0, 0.6)" } } }}
                                    InputProps={{
                                        endAdornment: filters.mobileNo && (
                                            <InputAdornment position="end">
                                                <IconButton onClick={() => handleClearField('mobileNo')} edge="end">
                                                    <CloseIcon />
                                                </IconButton>
                                            </InputAdornment>
                                        )
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6} md={3}>
                                <FormControl fullWidth size="small" sx={{ minWidth: 120 }}>
                                    <InputLabel sx={{ "&.Mui-focused": { color: "rgba(0, 0, 0, 0.6)" } }}>Platform</InputLabel>
                                    <Select
                                        label="Platform"
                                        name="platform"
                                        value={filters.platform}
                                        onChange={handleFilterChange}
                                        endAdornment={filters.platform && (
                                            <InputAdornment position="end" sx={{ mr: 2 }}>
                                                <IconButton onClick={() => handleClearField('platform')} edge="end" size="small">
                                                    <CloseIcon fontSize="small" />
                                                </IconButton>
                                            </InputAdornment>
                                        )}
                                    >
                                        <MenuItem value=""><em>All</em></MenuItem>
                                        <MenuItem value="android">Android</MenuItem>
                                        <MenuItem value="ios">iOS</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} sm={6} md={3}>
                                <FormControl fullWidth size="small" sx={{ minWidth: 120 }}>
                                    <InputLabel sx={{ "&.Mui-focused": { color: "rgba(0, 0, 0, 0.6)" } }}>Currency</InputLabel>
                                    <Select
                                        label="Currency"
                                        name="currency"
                                        value={filters.currency}
                                        onChange={handleFilterChange}
                                        endAdornment={filters.currency && (
                                            <InputAdornment position="end" sx={{ mr: 2 }}>
                                                <IconButton onClick={() => handleClearField('currency')} edge="end" size="small">
                                                    <CloseIcon fontSize="small" />
                                                </IconButton>
                                            </InputAdornment>
                                        )}
                                    >
                                        <MenuItem value=""><em>All</em></MenuItem>
                                        <MenuItem value="INR">INR</MenuItem>
                                        <MenuItem value="USD">USD</MenuItem>
                                        <MenuItem value="EUR">EUR</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} sm={6} md={3}>
                                <TextField
                                    fullWidth
                                    label="Start Date"
                                    name="startDate"
                                    type="date"
                                    InputLabelProps={{ shrink: true }}
                                    value={filters.startDate}
                                    onChange={handleFilterChange}
                                    size="small"
                                    InputProps={{
                                        endAdornment: filters.startDate && (
                                            <InputAdornment position="end">
                                                <IconButton onClick={() => handleClearField('startDate')} edge="end">
                                                    <CloseIcon />
                                                </IconButton>
                                            </InputAdornment>
                                        )
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <TextField
                                    fullWidth
                                    label="End Date"
                                    name="endDate"
                                    type="date"
                                    InputLabelProps={{ shrink: true }}
                                    value={filters.endDate}
                                    onChange={handleFilterChange}
                                    size="small"
                                    InputProps={{
                                        endAdornment: filters.endDate && (
                                            <InputAdornment position="end">
                                                <IconButton onClick={() => handleClearField('endDate')} edge="end">
                                                    <CloseIcon />
                                                </IconButton>
                                            </InputAdornment>
                                        )
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12} display="flex" justifyContent="flex-end" alignItems="center" gap="10px">
                                <Button variant="outlined" color="black" onClick={handleClearFilters}>Clear Filters</Button>
                            </Grid>
                        </Grid>
                    </Box>
                </div>

                {/* Data Table */}
                <div id='sales-dashboard-table'
                    style={{
                        marginTop: '10px',
                        flex: 1, // Take remaining space
                        overflow: 'hidden', // Hide container overflow
                        display: 'flex',
                        flexDirection: 'column',
                        padding: '0 20px',
                        marginBottom: '10px'
                    }}>
                    <TableContainer
                        component={Paper}
                        elevation={3}
                        sx={{
                            flex: 1, // Expand to fill flex container
                            overflow: 'auto', // Scroll only within the table
                            height: '100%', // Ensure it takes 100% of the parent flex height
                            display: 'flex',
                            flexDirection: 'column'
                        }}
                    >
                        <Table stickyHeader sx={{ minWidth: 650 }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell>User ID</TableCell>
                                    <TableCell>Platform</TableCell>
                                    <TableCell>Date</TableCell>
                                    <TableCell>Currency</TableCell>
                                    <TableCell>Phone Number</TableCell>
                                    <TableCell>Amount</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    <TableRow>
                                        <TableCell colSpan={6} align="center">
                                            <CircularProgress />
                                        </TableCell>
                                    </TableRow>
                                ) : transactions.length > 0 ? (
                                    transactions.map((txn) => (
                                        <TableRow key={txn._id} hover>
                                            <StyledTableCell>{txn.userId}</StyledTableCell>
                                            <StyledTableCell>{txn.platform || '-'}</StyledTableCell>
                                            <StyledTableCell>{formatDate(txn.created_at || txn.createdAt)}</StyledTableCell>
                                            <StyledTableCell>{txn.currency || '-'}</StyledTableCell>
                                            <StyledTableCell>{txn.userPhoneNumber || (txn.userDetails && (txn.userDetails.mobile || txn.userDetails.phoneNumber)) || '-'}</StyledTableCell>
                                            <StyledTableCell>{txn.amount}</StyledTableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={6} align="center">No transactions found</TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </div>

                {/* Pagination */}
                <div style={{ flexShrink: 0, paddingBottom: '20px' }}>
                    <Box display="flex" justifyContent="center" mt="20px">
                        <Pagination
                            count={totalPages}
                            page={page}
                            onChange={(e, value) => setPage(value)}
                            color="secondary"
                            showFirstButton
                            showLastButton
                        />
                    </Box>
                </div>
            </div>
        </div>
    );
};

export default SalesDashboard;
